<template>
  <div>
    <component :is="resolveLayoutVariant" :class="`skin-variant--${appSkinVariant}`" :isInEditor="isInEditor">
      <transition :name="appRouteTransition" mode="out-in" appear>
        <router-view :key="$route.path"></router-view>
      </transition>
      <async-notification-snackbar
        name="download"
        :message="t('Downloading')"
        :error-message="t('ErrorDownloadingFile')"
      />
      <async-notification-snackbar
          name="generate"
          message="Generating..."
          error-message="Error generating file"
        />
      <notification-card-snackbar
        top
        right
        class="app-notification-card-snackbar"
      />
      <editor-context-menu
        element-id="editorContextMenu"
        ref="vueSimpleContextMenu"
        @context-menu-option-clicked="contextMenuOptionClicked"
      />
      <survey-dialog
        v-if="isSurveyDialogActive"
        :is-dialog-visible="isSurveyDialogActive"
        :survey="survey"
        @close-dialog="isSurveyDialogActive = false"
      />
      <quick-guide-dialog
        v-if="quickGuideLoadedOnce && hasQuickGuide && quickGuidePending && showQuickGuideDialog"
        v-model="showQuickGuideDialog"
        :document-id="quickGuideDocumentId"
        :user-doc-id="quickGuideUserDocId"
        title="Quick guide"
        @accepted="onQuickGuideAccepted"
      />
    </component>
  </div>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import { ref, onMounted, computed, watch, } from 'vue'
// eslint-disable-next-line import/no-unresolved
import useAppConfig from '@core/@app-config/useAppConfig'
import { useRouter } from '@core/utils'
import { useLayout } from '@core/layouts/composable/useLayout'

// Layouts
import LayoutContentVerticalNav from '@/layouts/variants/content/vertical-nav/LayoutContentVerticalNav.vue'
import LayoutContentHorizontalNav from '@/layouts/variants/content/horizontal-nav/LayoutContentHorizontalNav.vue'
import LayoutBlank from '@/layouts/variants/blank/LayoutBlank.vue'
import LayoutAppBarVerticalNav from '@/layouts/variants/app-bar/vertical-nav/LayoutAppBarVerticalNav.vue'
import LayoutEditorVerticalNav from '@/layouts/variants/editor/vertical-nav/LayoutEditorVerticalNav.vue'

// Surveys
import SurveyDialog from 'lib-hermes-support/src/lib-components/surveys/dialogs/survey-dialog.vue'
import useSurvey from 'lib-hermes-support/src/lib-components/surveys/useSurvey'

// Notifications
import useNotification from 'lib-notification-vue/src/lib-components/useNotification'

// Dynamic vh
import useDynamicVh from '@core/utils/useDynamicVh'

// Snackbar notification
import AsyncNotificationSnackbar from 'lib-hermes-components/src/lib-components/notification-snackbar/async-notification-snackbar.vue'
import NotificationCardSnackbar from 'lib-hermes-components/src/lib-components/notification-card/notification-card-snackbar.vue'

// i18n
import { useUtils } from '@/plugins/i18n/utils'

import EditorContextMenu from 'lib-editor-vue/src/lib-components/context-menu/EditorContextMenu';
import { EventBus } from '@/plugins/event-bus';


import QuickGuideDialog from '@/components/dialogs/quick-guide-dialog.vue';
import settings from '@/components/settings/settings';
import Authentication from 'lib-security/src/lib-components/security.vue'


export default {
  components: {
    LayoutContentVerticalNav,
    LayoutContentHorizontalNav,
    LayoutBlank,
    LayoutAppBarVerticalNav,
    LayoutEditorVerticalNav,
    SurveyDialog,
    AsyncNotificationSnackbar,
    NotificationCardSnackbar,
    EditorContextMenu,
    QuickGuideDialog,
  },
  setup() {
    const { route } = useRouter()
    const { appContentLayoutNav, appSkinVariant, appRouteTransition } = useAppConfig()
    const { t } = useUtils()
    const { getActiveSurveyForUser } = useSurvey();
    const { quickGuideDocumentId, loadQuickGuideDocumentId, hasQuickGuide } = settings()
    const {fetchPendingQuickGuide, confirmQuickGuideRead, quickGuidePending, quickGuideUserDocId, quickGuideLoadedOnce } = useNotification();

    const { handleBreakpointLayoutSwitch } = useLayout()
    handleBreakpointLayoutSwitch()

    const resolveLayoutVariant = computed(() => {
      if (route.value.meta.layout === 'blank') return 'layout-blank'
      if (route.value.meta.layout === 'content' || route.value.meta.layout === 'settings' || route.value.meta.layout === 'editor')
        return `layout-content-${appContentLayoutNav.value}-nav`
      if (route.value.meta.layout === 'app-bar') return `layout-app-bar-${appContentLayoutNav.value}-nav`

      return null
    })

    useDynamicVh()
    
    const showQuickGuideDialog = ref(true)

    // Context menu

    const vueSimpleContextMenu = ref({});
    const editorRef = ref(null);
    const itemRef = ref(null);

    EventBus.$on('editor-right-click', (event, editor, item, document, latestDocumentVersion) => {
      //vueSimpleContextMenu.value.hideContextMenu()
      editorRef.value = editor;
      itemRef.value = item;
      vueSimpleContextMenu.value.showMenu(event, editor, item, document, latestDocumentVersion)
    })

    async function contextMenuOptionClicked (event) {
      event.option.action(editorRef.value, itemRef.value);
    }

    const isInEditor = computed(() => {
      return route.value.meta.layout === 'editor';
    })

    const isSurveyDialogActive = ref(false);
    const survey = ref(null);

    
    onMounted(async () => {
      try {
        if (userData.value.roles.includes('hermes-support')) {
          const data = await getActiveSurveyForUser();
          if (data && data.id && data.surveyJson) {
            survey.value = data
            isSurveyDialogActive.value = true
            return
          }
        }
      } catch (e) {
        console.error('Error fetching active survey', e)
      }

      await checkAndShowQuickGuide()
    })
 
    async function onCloseSurveyDialog() {
      isSurveyDialogActive.value = false
      survey.value = null
      await checkAndShowQuickGuide()
    }

    async function checkAndShowQuickGuide() {
      try {
        await loadQuickGuideDocumentId();

        if (!hasQuickGuide.value) {
          showQuickGuideDialog.value = false;
          return;
        }

        await fetchPendingQuickGuide()

      } catch (e) {
        console.error('Error checking Quick Guide pending', e)
        showQuickGuideDialog.value = false
      }
    }

    async function onQuickGuideAccepted({ id }) {
      try {
        await confirmQuickGuideRead(id)
      } catch (e) {
        console.error('Error confirming quick guide read', e)
      } finally {
        showQuickGuideDialog.value = false
      }
    }

    const userData = computed(() => {
      var tokenParsed = {};
      try {
        var tokenParsed = Authentication.methods.getTokenParsed();
        tokenParsed.roles = tokenParsed.realm_access.roles;
      } catch (error) {
        console.log(error);
      } finally {
        return tokenParsed;
      }
    });

    return {
      t,
      resolveLayoutVariant,
      appSkinVariant,
      appRouteTransition,
      vueSimpleContextMenu,
      contextMenuOptionClicked,
      isInEditor,
      isSurveyDialogActive,
      survey,

      showQuickGuideDialog,
      quickGuideUserDocId,
      quickGuideDocumentId,
      onCloseSurveyDialog,
      onQuickGuideAccepted,
      quickGuideLoadedOnce,
      quickGuidePending,
      hasQuickGuide,

    }
  },
}
</script>
<style lang="scss">
.app-notification-card-snackbar {
  margin-top: 82px;
  margin-right: 1.5rem;
  .v-snack__wrapper {
    margin: 0px;
  }
}
</style>